import React from 'react';
import { Clock3, CreditCard, Droplets } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const safeMetric = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
};

const PREFERENCE_GROUPS = [
  { key: 'waterTypes', icon: Droplets, accent: 'cyan', itemKeys: ['pure', 'mineral', 'unknown'] },
  { key: 'cardChannels', icon: CreditCard, accent: 'emerald', itemKeys: ['physical', 'virtual', 'unknown'] },
  { key: 'timeBuckets', icon: Clock3, accent: 'amber', itemKeys: ['overnight', 'morning', 'afternoon', 'evening', 'unknown'] }
];

const accentClasses = {
  cyan: { icon: 'text-cyan-300', bar: 'bg-cyan-400', badge: 'bg-cyan-500/15 text-cyan-300' },
  emerald: { icon: 'text-emerald-300', bar: 'bg-emerald-400', badge: 'bg-emerald-500/15 text-emerald-300' },
  amber: { icon: 'text-amber-300', bar: 'bg-amber-400', badge: 'bg-amber-500/15 text-amber-300' }
};

const PreferenceGroup = ({ definition, value, locale }) => {
  const { t } = useTranslation();
  const Icon = definition.icon;
  const classes = accentClasses[definition.accent];
  const preferredKey = value?.preferredKey || value?.peakKey || null;
  const items = value?.items || {};

  return (
    <section className="admin-muted-panel rounded-2xl p-4" aria-label={t(`unitDetail.operationalAnalysis.preference.groups.${definition.key}`)}>
      <h4 className="admin-text-primary flex items-center gap-2 text-sm font-black">
        <Icon size={16} className={classes.icon} aria-hidden="true" />
        {t(`unitDetail.operationalAnalysis.preference.groups.${definition.key}`)}
      </h4>
      <div className="mt-4 space-y-3">
        {definition.itemKeys.map((key) => {
          const item = items[key] || {};
          const count = safeMetric(item.count);
          const countPercent = Math.min(safeMetric(item.countPercent), 100);
          const volume = safeMetric(item.volume);
          const isPreferred = count > 0 && key === preferredKey;

          return (
            <div key={key}>
              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 text-xs">
                <div>
                  <span className="admin-text-primary whitespace-nowrap font-bold">
                    {t(`unitDetail.operationalAnalysis.preference.labels.${definition.key}.${key}`)}
                  </span>
                  {isPreferred && (
                    <span className={`ml-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-black ${classes.badge}`}>
                      {t('unitDetail.operationalAnalysis.preference.preferred')}
                    </span>
                  )}
                </div>
                <span className="admin-text-secondary shrink-0 text-right">
                  {t('unitDetail.operationalAnalysis.preference.recordCount', {
                    count: count.toLocaleString(locale),
                    percent: countPercent.toLocaleString(locale, { maximumFractionDigits: 2 })
                  })}
                </span>
              </div>
              <div
                className="mt-2 h-2 overflow-hidden rounded-full bg-slate-700/60"
                role="progressbar"
                aria-label={t(`unitDetail.operationalAnalysis.preference.labels.${definition.key}.${key}`)}
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={countPercent}
              >
                <div className={`h-full rounded-full ${classes.bar}`} style={{ width: `${countPercent}%` }} />
              </div>
              <p className="admin-text-muted mt-1 text-[10px]">
                {t('unitDetail.operationalAnalysis.preference.volume', {
                  volume: volume.toLocaleString(locale, { maximumFractionDigits: 2 })
                })}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const UnitPreferencePanel = ({ preference }) => {
  const { t, i18n } = useTranslation();
  const sampleSize = safeMetric(preference?.sampleSize);
  const locale = i18n.resolvedLanguage || i18n.language || 'en';

  return (
    <section className="admin-card rounded-3xl p-6" data-testid="unit-preference-panel">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="admin-text-primary text-sm font-black">
            {t('unitDetail.operationalAnalysis.preference.title')}
          </h3>
          <p className="admin-text-secondary mt-1 text-xs leading-relaxed">
            {t('unitDetail.operationalAnalysis.preference.description')}
          </p>
        </div>
        <span className="shrink-0 self-start rounded-full bg-cyan-500/15 px-3 py-1 text-[11px] font-black text-cyan-300">
          {t('unitDetail.operationalAnalysis.preference.sample', { count: sampleSize.toLocaleString(locale) })}
        </span>
      </div>

      {sampleSize === 0 ? (
        <div className="admin-muted-panel admin-text-secondary mt-4 rounded-2xl border-dashed p-6 text-center text-sm">
          {t('unitDetail.operationalAnalysis.preference.empty')}
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {PREFERENCE_GROUPS.map((definition) => (
            <PreferenceGroup key={definition.key} definition={definition} value={preference?.[definition.key]} locale={locale} />
          ))}
        </div>
      )}
    </section>
  );
};

export default UnitPreferencePanel;
